import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Título atual — o campo abre preenchido com ele. */
  nome: string;
  /** Rótulo do botão que conclui: "Salvar" ou "Salvar e sair". */
  confirmLabel: string;
  saving: boolean;
  onConfirm: (nome: string) => void;
}

/**
 * Confirmação do título antes de salvar. O input do cabeçalho do construtor
 * não deixa óbvio que dá pra renomear (o pessoal salvava tudo como "Nova
 * segmentação"), então todo salvar passa por aqui com o nome atual já
 * preenchido e selecionado — é só digitar por cima ou confirmar.
 */
export function SegmentationSaveDialog({
  open,
  onOpenChange,
  nome,
  confirmLabel,
  saving,
  onConfirm,
}: Props) {
  const [value, setValue] = useState(nome);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cada abertura repõe o título atual (pode ter mudado no cabeçalho).
  useEffect(() => {
    if (!open) return;
    setValue(nome);
    setError(null);
  }, [open, nome]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Dê um nome para a audiência.');
      inputRef.current?.focus();
      return;
    }
    onConfirm(trimmed);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Sem fechar no meio do request: o toast de sucesso/erro é quem encerra.
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-md"
        // Abre com o nome inteiro selecionado — digitar já substitui.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
          inputRef.current?.select();
        }}
      >
        <DialogHeader>
          <DialogTitle>Nome da audiência</DialogTitle>
          <DialogDescription>
            Confira o nome antes de salvar, é por ele que você acha a audiência na lista e na
            hora de escolher o público da campanha.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="segmentationNome">Título</Label>
            <Input
              id="segmentationNome"
              ref={inputRef}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                if (error) setError(null);
              }}
              placeholder="Ex.: Inativos há mais de 90 dias"
              maxLength={120}
              autoComplete="off"
              disabled={saving}
            />
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
